import { isEmpty } from '@shared/common'
import { Types } from 'service-api'

export const getGroupMinimumPrice = (
  availabilityGroups: Types.AvailabilityGroupClientDto[][] | undefined
): Types.AvailabilityGroupClientDto => {
  if (!availabilityGroups || availabilityGroups.length == 0 || availabilityGroups[0].length == 0) {
    return {}
  }

  return availabilityGroups.reduce(
    (acc, group) => {
      const offerGroup = group[0].availabilities![0]!
      const accGroup = acc.availabilities![0]!.price!.value

      // We do not want to show price if unavailableReason is MBV
      if (offerGroup.unavailableReason?.type === 'PRICE' || isEmpty(offerGroup.price?.value)) {
        return acc
      }
      return accGroup === undefined || offerGroup.price!.value! < accGroup ? group[0] : acc
    },
    { availabilities: [{ price: { value: undefined } }] } as Types.AvailabilityGroupClientDto
  )
}

export const getBaseGroup = (
  serviceTypeName: string,
  petGroup: number,
  availabilityGroups: Types.AvailabilityGroupClientDto[] | Types.AvailabilityGroupClientDto[][] | undefined
): Types.AvailabilityGroupClientDto => {
  if (!availabilityGroups) {
    return {}
  }

  if (serviceTypeName === 'daycare') {
    const tempTestRequiredOffers = (availabilityGroups as Types.AvailabilityGroupClientDto[][]).filter(
      (ag) =>
        ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' ||
        ag?.[0]?.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
    )
    const applicableOffers = tempTestRequiredOffers.length > 0 ? tempTestRequiredOffers : availabilityGroups
    return getGroupMinimumPrice(applicableOffers as Types.AvailabilityGroupClientDto[][])
  }

  return (availabilityGroups as Types.AvailabilityGroupClientDto[])?.[petGroup] || {}
}

export const isDaycareOfferUnavailable = (availabilityGroups: Types.AvailabilityGroupClientDto[][] | undefined) => {
  let allDaysUnavailable = true,
    atLeastOneUnavailableReasonApplies = false
  availabilityGroups?.forEach((group) => {
    const availability = group?.[0]?.availabilities?.[0]
    if (availability?.available) {
      allDaysUnavailable = false
    }
    if (
      ['WEIGHT', 'TOTAL_WEIGHT', 'PET_COUNT', 'SALES_CHANNEL', 'OCCUPANCY'].includes(
        availability?.unavailableReason?.type || ''
      ) ||
      (availability?.unavailableReason?.type === 'TEMPER_TEST' &&
        availability.condition?.reason?.type !== 'TEMPER_TEST')
    ) {
      atLeastOneUnavailableReasonApplies = true
    }
  })
  return allDaysUnavailable && atLeastOneUnavailableReasonApplies
}

export const isDaycareOfferAvailableWithTempTest = (
  availabilityGroups: Types.AvailabilityGroupClientDto[][] | undefined
) => {
  return availabilityGroups?.some(
    (ag) =>
      (ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' &&
        ag?.[0]?.availabilities?.[0].unavailableReason?.contingent) ||
      ag?.[0]?.availabilities?.[0].condition?.reason?.type === 'TEMPER_TEST'
  )
}

export const isDaycareOfferUnavailableWithTempTestFail = (
  availabilityGroups: Types.AvailabilityGroupClientDto[][] | undefined
) => {
  return availabilityGroups?.some(
    (ag) =>
      ag?.[0]?.availabilities?.[0].unavailableReason?.type === 'TEMPER_TEST' &&
      ag?.[0]?.availabilities?.[0].unavailableReason?.temperTestFailed
  )
}

export const getRoomPrice = (
  availabilityGroup: Types.AvailabilityGroupClientDto,
  isDaycare: boolean,
  totalPets: number | undefined
) => {
  const room = availabilityGroup.availabilities?.[0]
  if (!room || room.unavailableReason?.type === 'PRICE') {
    // We do not want to show price if unavailableReason is MBV
    return undefined
  }

  if (isDaycare) {
    return isEmpty(room.price?.value) ? undefined : room.price!.value! / (totalPets ?? 1)
  }

  return room.price?.rate
}

export const getTemperTestBadgeText = (
  availabilityGroups: Types.AvailabilityGroupClientDto[] | Types.AvailabilityGroupClientDto[][],
  isDaycare: boolean
): string | undefined | null => {
  const thisGroup: Types.AvailabilityGroupClientDto = isDaycare
    ? (availabilityGroups
        .flat()
        .filter((g) => g?.availabilities?.[0].temperTestMsg)[0] as Types.AvailabilityGroupClientDto)
    : (availabilityGroups[0] as Types.AvailabilityGroupClientDto)

  return thisGroup?.availabilities?.[0].temperTestMsg
}
